import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { isInstalledConnection } from 'app/connections/types'
import {
  GoogleAdwordsAdAccount,
  GoogleAdwordsConnection,
  GoogleAdwordsConnectionConfig,
  InstalledGoogleAdwordsConnection,
} from 'app/connections/types/googleAdwords'

import { DropDown } from 'components'

import { Authorization } from '../../authorization'
import styles from '../Settings.module.scss'
import { AccountsSettings, DangerZone, SettingsSection } from '../components'

interface GoogleAdwordsSettingsProps {
  connection: GoogleAdwordsConnection | InstalledGoogleAdwordsConnection
  connectionConfig: GoogleAdwordsConnectionConfig
  onConnectionConfigUpdate: (key: string, value: GoogleAdwordsAdAccount[] | string) => void
  onConnectionConfigValidationUpdate: (isValidConfig: boolean) => void
}

export const GoogleAdwordsSettings = ({
  connection,
  connectionConfig,
  onConnectionConfigUpdate,
  onConnectionConfigValidationUpdate,
}: GoogleAdwordsSettingsProps) => {
  const { t } = useTranslation('connections')

  const updateConfig = (
    key: Extract<keyof GoogleAdwordsConnectionConfig, string>,
    value: GoogleAdwordsAdAccount[] | string,
  ) => onConnectionConfigUpdate(key, value)

  const removeAccount = (account: GoogleAdwordsAdAccount) =>
    updateConfig(
      'accounts',
      connectionConfig.accounts.filter(({ id }) => id !== account.id),
    )

  const campaignIds = connectionConfig.campaignId ? [connectionConfig.campaignId] : []

  useEffect(() => {
    onConnectionConfigValidationUpdate(
      connectionConfig.accounts.length > 0 && !!connectionConfig.campaignId,
    )
  }, [connectionConfig, onConnectionConfigValidationUpdate])

  return (
    <>
      <SettingsSection
        contentClassName={styles.basicCard}
        description={t('setup.basics.description')}
        title={t('setup.basics.title')}
      >
        <Authorization connection={connection} />
      </SettingsSection>
      <AccountsSettings
        accounts={connectionConfig.accounts}
        connectionName={connection.name}
        onRemove={removeAccount}
      />
      <SettingsSection
        contentClassName={styles.idsCard}
        description={t('manage.meta.ids.description')}
        title={t('manage.meta.ids.title')}
      >
        <label>
          <span>{t('setup.googleAdwords.config.campaignId.title')}</span>
          <DropDown
            data={campaignIds}
            onSelect={(campaignId) => updateConfig('campaignId', campaignId)}
            placeholder={t('setup.googleAdwords.config.campaignId.title')}
            selected={connectionConfig.campaignId || null}
          />
        </label>
      </SettingsSection>
      {isInstalledConnection(connection) && <DangerZone connection={connection} />}
    </>
  )
}
